import React, { useEffect, useRef, useState } from 'react';
import { gantt } from 'dhtmlx-gantt';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
import Button from 'components/base/Button';

import { useCalendarContext } from 'providers/CalendarProvider';
import { SET_CALENDAR_STATE } from 'reducers/CalendarReducer';

import FeatherIcon from 'feather-icons-react';
import { ButtonGroup } from 'react-bootstrap';

import dayjs from 'dayjs';
import { useAppContext } from 'providers/AppProvider';
import { useCarManagmentContext } from 'providers/CarManagmentProvider';
import { getPlannerWeekRange } from 'helpers/utils';

// --- Constants (Extracted Magic Numbers) ---
const DAY_START_HOUR = 8;
const DAY_END_HOUR = 19;
const WEEK_START_HOUR = 0;
// const WEEK_END_HOUR = 24; // 24:00 of the last day
const WORK_HOURS = [`${DAY_START_HOUR}:00`, `${DAY_END_HOUR}:00`];
const WEEK_DAYS_TO_SHOW = 5; // Monday to Friday

// --- Interfaces (from original code) ---
export interface Trip {
  id: number | string;
  grupo: number | string;
  text: string;
  passenger: string;
  listPassenger: string;
  outsideUsers: number;
  start_date: Date;
  end_date: Date;
  duration: number;
  carPlate: string;
  originalStart: Date;
  originalEnd: Date;
  isClipped: boolean;
  takeCar: 'Y' | 'N';
  leaveCar: 'Y' | 'N';
  canChangeTime?: 'Y' | 'N';
}

interface GanttTask {
  id: string | number;
  text: string;
  open?: boolean;
  render?: string;
  parent?: string;
  start_date?: Date;
  end_date?: Date;
  duration?: number;
  passenger?: string;
  listPassenger?: string;
  carPlate?: string;
  sameCarSameDayPassengers?: string[];
  originalStart?: Date;
  originalEnd?: Date;
  is_skeleton?: boolean; //last update
  isClipped?: boolean;
  isPending?: boolean;
  canChangeTime?: 'Y' | 'N';
  type?: string; // Added for project/task distinction
}

interface GanttData {
  data: GanttTask[];
  links: [];
}

export interface GanttChartProps {
  trips: Trip[];
  pendingTripIds?: (string | number)[];
  initialDate?: Date;
  initialViewMode?: 'day' | 'week';
  onPlannerDateChange?: (date: Date) => void;
  onCancelTrip?: (tripId: string | number) => void;
  rightColumn?: 'name' | 'carPlate';
}

// --- Consolidated Helper Functions ---

// --- React Component ---

const BasicGanttChart: React.FC<GanttChartProps> = ({
  trips,
  pendingTripIds = [],
  initialDate = new Date(),
  initialViewMode = 'day',
  onPlannerDateChange,
  onCancelTrip,
  rightColumn
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hoverTaskIdRef = useRef<string | null>(null);
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [viewMode, setViewMode] = useState<'day' | 'week'>(initialViewMode);
  const [hoverTooltip, setHoverTooltip] = useState<{
    x: number;
    y: number;
    title: string;
    lines: string[];
  } | null>(null);
  const { calendarDispatch } = useCalendarContext();
  const { getCurrentUser } = useAppContext();
  const { isLoadingTrips, isLoadingTripRange, carsTrips } =
    useCarManagmentContext();
  const user = getCurrentUser();

  const clipTrip = (
    trip: Trip,
    startBoundary: Date,
    endBoundary: Date //,
    // boundaryId: number
  ): Trip | null => {
    const tripStart = new Date(trip.start_date);
    const tripEnd = new Date(
      tripStart.getTime() + trip.duration * 60 * 60 * 1000
    );

    const overlaps = tripStart < endBoundary && tripEnd > startBoundary;
    if (!overlaps) return null;

    const visibleStart = new Date(
      Math.max(tripStart.getTime(), startBoundary.getTime())
    );
    const visibleEnd = new Date(
      Math.min(tripEnd.getTime(), endBoundary.getTime())
    );

    const visibleDuration = gantt.calculateDuration({
      start_date: visibleStart,
      end_date: visibleEnd
    });

    const isClipped = tripStart < startBoundary || tripEnd > endBoundary;

    return {
      ...trip,
      start_date: visibleStart,
      end_date: visibleEnd,
      duration: visibleDuration,
      originalStart: tripStart,
      originalEnd: tripEnd,
      isClipped: isClipped,
      id: trip.id
      // id: isClipped ? `${trip.id}-clipped-${boundaryId}` : trip.id
    };
  };

  const parseNames = (value?: string): string[] => {
    if (!value) return [];

    return value
      .split(',')
      .map(name => name.trim())
      .filter(name => name.length > 0 && name !== 'undefined');
  };

  const getDateKey = (date?: Date): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const transformTripsToGanttData = (
    trips: Trip[],
    pendingIds: Set<string | number>,
    rightColumn?: 'name' | 'carPlate'
  ): GanttData => {
    const ganttData: GanttTask[] = [];

    const groupMap = new Map<string, string>();
    const carDayPassengers = new Map<string, Set<string>>();

    if (rightColumn === 'carPlate') {
      trips.forEach(trip => {
        const carPlate = `${trip.carPlate ?? ''}`.trim();
        if (!carPlate) return;

        const dateKey = getDateKey(trip.start_date);
        const mapKey = `${carPlate}_${dateKey}`;
        const existing = carDayPassengers.get(mapKey) ?? new Set<string>();

        parseNames(trip.passenger).forEach(name => existing.add(name));

        if (existing.size > 0) {
          carDayPassengers.set(mapKey, existing);
        }
      });

      carsTrips.forEach(car => {
        const carPlate = `${car.carPlate ?? ''}`.trim();

        if (!carPlate || groupMap.has(carPlate)) return;

        groupMap.set(carPlate, carPlate);
        ganttData.push({
          id: carPlate,
          text: carPlate,
          passenger: '',
          open: true,
          type: 'project',
          render: 'split'
        });
      });
    }

    trips.forEach(trip => {
      if (rightColumn === 'carPlate') {
        const carPlate = `${trip.carPlate ?? ''}`.trim();

        if (carPlate && !groupMap.has(carPlate)) {
          groupMap.set(carPlate, carPlate);
          ganttData.push({
            id: carPlate,
            text: carPlate,
            passenger: trip.passenger,
            open: true,
            type: 'project',
            render: 'split'
          });
        }
      } else if (rightColumn === 'name') {
        if (!groupMap.has(trip.passenger)) {
          const name = `${trip.passenger}`;
          groupMap.set(trip.passenger, name);
          ganttData.push({
            id: name,
            text: trip.carPlate,
            passenger: trip.passenger,
            open: true,
            type: 'project',
            render: 'split'
          });
        }
      }
    });

    trips.forEach(trip => {
      const carPlate = `${trip.carPlate ?? ''}`.trim();
      const dateKey = getDateKey(trip.start_date);
      const mapKey = `${carPlate}_${dateKey}`;
      const passengerSet = carDayPassengers.get(mapKey);

      ganttData.push({
        ...trip,
        parent:
          rightColumn == 'name'
            ? groupMap.get(trip.passenger)
            : groupMap.get(trip.carPlate),
        sameCarSameDayPassengers: passengerSet
          ? Array.from(passengerSet)
          : undefined,
        isPending: pendingIds.has(trip.id)
      });
    });

    return {
      data: ganttData,
      links: []
    };
  };

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const formatTooltipDate = (date?: Date | string) => {
    if (!date) return '';
    const parsed = typeof date === 'string' ? new Date(date) : date;
    return parsed.toLocaleString('pt-PT', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPassengersAssignedAtTime = (task: GanttTask): string[] => {
    if (!task.carPlate || !task.start_date || !task.end_date) return [];

    const taskStart = task.start_date;
    const taskEnd = task.end_date;
    const passengers = new Set<string>();

    trips.forEach(trip => {
      if (`${trip.carPlate ?? ''}`.trim() !== `${task.carPlate}`.trim()) {
        return;
      }

      const tripStart = new Date(trip.start_date);
      const tripEnd = new Date(
        tripStart.getTime() + trip.duration * 60 * 60 * 1000
      );
      const overlaps = tripStart < taskEnd && tripEnd > taskStart;

      if (overlaps) {
        parseNames(trip.passenger).forEach(passenger =>
          passengers.add(passenger)
        );
      }
    });

    return Array.from(passengers);
  };

  const getGanttTaskTooltip = (task: GanttTask) => {
    if (!task) return null;
    const lines: string[] = [];
    const title =
      rightColumn === 'name'
        ? `Carro: ${task.text}`
        : `Passageiros: ${task.passenger ?? 'Por definir'}`;

    const assignedPassengers = getPassengersAssignedAtTime(task);
    if (assignedPassengers.length) {
      lines.push(`Pessoas: ${assignedPassengers.join(', ')}`);
    } else if (task.listPassenger) {
      lines.push(`Pessoas: ${task.listPassenger}`);
    } else if (task.passenger) {
      lines.push(`Pessoas: ${task.passenger}`);
    }

    if (task.text) {
      lines.push(`Local: ${task.text}`);
    }

    if (task.start_date && task.end_date) {
      lines.push(
        `Horário: ${formatTooltipDate(task.start_date)} - ${formatTooltipDate(task.end_date)}`
      );
    }

    return { title, lines };
  };

  const handleMouseMove = (event: MouseEvent) => {
    const container = containerRef.current;
    if (!container) return;

    const target = event.target as HTMLElement;
    const taskElement = target.closest('[data-task-id]') as HTMLElement | null;
    if (!taskElement) {
      hoverTaskIdRef.current = null;
      setHoverTooltip(null);
      return;
    }

    const taskId = taskElement.getAttribute('data-task-id');
    if (!taskId) {
      hoverTaskIdRef.current = null;
      setHoverTooltip(null);
      return;
    }

    const bounds = container.getBoundingClientRect();
    const tooltipWidth = 280;
    const offset = 12;
    const rawX = event.clientX - bounds.left + offset;
    const overflowRight = rawX + tooltipWidth > bounds.width;
    const x = overflowRight
      ? Math.max(event.clientX - bounds.left - tooltipWidth - offset, offset)
      : rawX;
    const y = event.clientY - bounds.top + offset;
    const position = { x, y };

    if (hoverTaskIdRef.current === taskId) {
      setHoverTooltip(prev => (prev ? { ...prev, ...position } : prev));
      return;
    }

    const task = gantt.getTask(taskId) as GanttTask | null;
    if (!task || task.type === 'project' || task.is_skeleton) {
      hoverTaskIdRef.current = null;
      setHoverTooltip(null);
      return;
    }

    hoverTaskIdRef.current = taskId;
    const tooltip = getGanttTaskTooltip(task);
    if (!tooltip) {
      setHoverTooltip(null);
      return;
    }

    setHoverTooltip({
      ...tooltip,
      ...position
    });
  };

  const handleMouseLeave = () => {
    hoverTaskIdRef.current = null;
    setHoverTooltip(null);
  };

  const goToNext = () => {
    setCurrentDate(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() + (viewMode === 'day' ? 1 : 7));

      while (isWeekend(next)) {
        next.setDate(next.getDate() + 1);
      }

      return next;
    });
  };

  const goToPrevious = () => {
    setCurrentDate(prev => {
      const previous = new Date(prev);
      previous.setDate(previous.getDate() - (viewMode === 'day' ? 1 : 7));

      while (isWeekend(previous)) {
        previous.setDate(previous.getDate() - 1);
      }

      return previous;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isSameCalendarDay = (dateA: Date, dateB: Date) => {
    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  };

  const isSameCalendarWeek = (dateA: Date, dateB: Date) => {
    const startOfWeek = (date: Date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      const day = d.getDay();
      const diff = (day + 6) % 7; // Monday-based week start
      d.setDate(d.getDate() - diff);
      return d;
    };

    const startA = startOfWeek(dateA).getTime();
    const startB = startOfWeek(dateB).getTime();
    return startA === startB;
  };

  useEffect(() => {
    onPlannerDateChange?.(new Date(currentDate));
  }, [currentDate, onPlannerDateChange]);

  // 1. Static Configuration and Initialization (Runs only once)
  useEffect(() => {
    if (!containerRef.current) return;

    // --- Configuration (Static) ---
    gantt.plugins({
      tooltip: false,
      quick_info: false,
      drag_timeline: true
    });
    // gantt.config.show_quick_info = false;

    gantt.config.scale_height = 50;
    gantt.config.row_height = 45;
    gantt.config.bar_height = 35;

    gantt.config.duration_unit = 'minute';
    gantt.config.duration_step = 1;
    gantt.config.date_format = '%Y-%m-%d %H:%i';

    gantt.config.readonly = true;
    gantt.config.open_tree_initially = true;

    gantt.i18n.setLocale('pt');

    gantt.attachEvent('onTaskClick', function (id) {
      // 1. Get the task data
      const task = gantt.getTask(id);

      if (typeof task.id == 'number') {
        if (
          rightColumn == 'name' &&
          !dayjs(task.originalEnd).isBefore(dayjs()) &&
          task.parent == user?.name
        ) {
          calendarDispatch({
            type: SET_CALENDAR_STATE,
            payload: {
              openNewEventModal: true,
              newEventModalMode: 'edit',
              updatingTrip: task
            }
          });
        }
      }
      return false;
    });

    gantt.config.columns = [
      {
        name: 'text',
        label: rightColumn == 'name' ? `Passageiros` : `Carro`,
        align: rightColumn == 'name' ? `left` : `center`,
        width: 100, //needed to have changing width
        // max_width: 85, //actual width of the element
        template: (task: GanttTask) => {
          const format =
            rightColumn == 'name'
              ? `<strong style="display:block; line-height: 1.45; align-self: center;">${task.passenger}</strong>`
              : `<strong style="display:block; margin-inline: auto;">${task.text}</strong>`;

          return `
            <div style="
              height: 100%;
              width: 100%;
              display: flex;
              padding: 2px;
              white-space: normal;
              "
            >
              ${format}
            </div>`;
        }
      }
    ];

    // NOTE: task_text template is dynamic and will be re-applied in the second useEffect
    // to capture the correct view boundaries.

    gantt.templates.task_class = (start: Date, end: Date, task: GanttTask) => {
      const classes = [];
      if (task.type === 'project') classes.push('hide-project-bar');
      if (task.is_skeleton) return 'gantt_skeleton_row'; //last update
      return classes.join(' ');
    };

    //last update
    gantt.templates.grid_item_class = function (task: GanttTask) {
      if (task.is_skeleton) return 'gantt_skeleton_column';
      return '';
    };
    //last update

    // Initialize Gantt on the container
    gantt.init(containerRef.current);

    containerRef.current.addEventListener('mousemove', handleMouseMove);
    containerRef.current.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('mousemove', handleMouseMove);
        containerRef.current.removeEventListener(
          'mouseleave',
          handleMouseLeave
        );
      }

      // Cleanup is necessary for the global gantt instance
      try {
        gantt.clearAll();
      } catch (e) {
        console.error('Cleanup error:', e);
      }
    };
  }, [onCancelTrip, rightColumn]); // Added rightColumn to dependencies for tooltip label changes

  //last update
  const generateSkeletons = (count = 10) => {
    const weekStart = gantt.date.week_start(new Date());

    return Array.from({ length: count }, (_, i) => ({
      id: `skeleton_${i}`,
      text: '', // Empty text
      passenger: '',
      start_date: weekStart,
      duration: 1440 * 5,
      is_skeleton: true // Custom flag for our CSS
    }));
  };
  //last update

  useEffect(() => {
    let filteredTrips: (Trip | null)[];
    let startDate: Date;
    let endDate: Date;

    if (viewMode === 'day') {
      gantt.config.work_time = true;
      gantt.setWorkTime({ hours: WORK_HOURS });

      const dayStart = new Date(currentDate);
      dayStart.setHours(DAY_START_HOUR, 0, 0, 0);
      const dayEnd = new Date(currentDate);
      dayEnd.setHours(DAY_END_HOUR, 0, 0, 0);
      startDate = dayStart;
      endDate = dayEnd;
      filteredTrips = trips.map(
        trip => clipTrip(trip, dayStart, dayEnd) //, currentDate.getTime())
      );
      gantt.config.scales = [
        { unit: 'day', step: 1, format: '%D, %d %M' },
        { unit: 'hour', step: 1, format: '%Hh' }
      ];
    } else {
      gantt.config.work_time = false;

      const { startDate: weekStart } = getPlannerWeekRange(currentDate);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + WEEK_DAYS_TO_SHOW); // Go to the day AFTER Friday (Saturday)
      weekEnd.setHours(WEEK_START_HOUR, 0, 0, 0); // Set to Saturday 00:00
      startDate = weekStart;
      endDate = weekEnd;
      filteredTrips = trips.map(
        trip => clipTrip(trip, weekStart, weekEnd) // , weekStart.getTime())
      );
      gantt.config.scales = [{ unit: 'day', step: 1, format: '%D, %d %M' }];
    }

    gantt.config.start_date = startDate;
    gantt.config.end_date = endDate;

    // Re-apply task_text template to capture new currentDate and viewMode boundaries
    gantt.templates.task_text = (start: Date, end: Date, task: GanttTask) => {
      // ✨ FIX 2: Prevent task_text from running on project/parent tasks
      if (task.type === 'project') {
        return '';
      }

      const content =
        rightColumn === 'name'
          ? task.text
          : (task.passenger
              ?.split(', ')
              .map((name: string) => `<span>${name}</span>`)
              .join('') ?? '');

      return `
    <div style="
      padding-inline: 8px;
      white-space: normal;"
    >
      ${content}
    </div>`;
    };

    const finalTrips = filteredTrips.filter(
      (trip): trip is Trip => trip !== null
    );

    const pendingIdsSet = new Set(pendingTripIds);
    const ganttData = transformTripsToGanttData(
      finalTrips,
      pendingIdsSet,
      rightColumn
    );

    // Update data and render
    gantt.clearAll();
    // last update
    if (trips.length < 1 && (isLoadingTrips || isLoadingTripRange)) {
      gantt.parse({ data: generateSkeletons() });
    } else {
      gantt.parse(ganttData);
    }
    gantt.render();

    gantt.resetLayout();
  }, [
    currentDate,
    viewMode,
    trips,
    // pendingTripIds,
    rightColumn,
    carsTrips,
    isLoadingTrips,
    isLoadingTripRange
  ]);

  return (
    <div>
      <div className="gantt-chart-button-container">
        <div className="selection-buttons-container">
          <Button
            className="item-content"
            onClick={() => {
              calendarDispatch({
                type: SET_CALENDAR_STATE,
                payload: {
                  openNewEventModal: true,
                  newEventModalMode: 'create'
                }
              });
            }}
            variant="primary"
            size="sm"
          >
            Fazer um pedido
          </Button>
          {/* </div>
          {/* forward/backward on weeks/day
          <div className=""> */}
          <ButtonGroup className="item-content">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={goToPrevious}
              // style={{ marginInline: '5px' }}
            >
              {/* {viewMode === 'day' ? 'Dia anterior' : 'Semana anterior'} */}
              <FeatherIcon icon="chevron-left" size={16} />
            </Button>
            <Button
              className="middle-button"
              variant="outline-primary"
              size="sm"
              onClick={goToToday}
              // style={{ marginInline: '5px' }}
            >
              {viewMode === 'day'
                ? isSameCalendarDay(currentDate, new Date())
                  ? 'Dia atual'
                  : 'Ir para Hoje'
                : isSameCalendarWeek(currentDate, new Date())
                  ? 'Semana atual'
                  : 'Ir para semana atual'}
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={goToNext}
              // style={{ marginInline: '5px' }}
            >
              {/* {viewMode === 'day' ? 'Próximo dia' : 'Próxima semana'} */}
              <FeatherIcon icon="chevron-right" size={16} />
            </Button>
          </ButtonGroup>
          {/* </div>
          Mudar semana/dia
          <div className=""> */}
          <ButtonGroup className="item-content">
            <Button
              variant={viewMode === 'day' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('day')}
              // className="btn btn-primary btn-sm"
            >
              Plano diário
            </Button>
            <Button
              variant={viewMode === 'day' ? 'outline-primary' : 'primary'}
              // size="sm"
              // style={{ marginInline: '5px' }}
              onClick={() => setViewMode('week')}
              // className="btn btn-primary btn-sm"
            >
              Plano semanal
            </Button>
          </ButtonGroup>
        </div>
      </div>
      <style>{`
        .hide-project-bar .gantt_task_content {
          display: none;
        }
        .gantt_task_line.hide-project-bar {
          display: none !important;
        }
        .gantt_task_line {
          background-color: #1976D2;
          border: 1px solid #2196F3;
          border-radius: 0px;
        }
        .gantt_task_line:hover {
          background-color: #2196F3;
        }
        .gantt_tree_indent {
          width: 0px !important;
        }
        .gantt-tooltip {
          position: absolute;
          z-index: 1050;
          max-width: 280px;
          background: rgba(33, 33, 33, 0.95);
          color: white;
          border-radius: 6px;
          padding: 10px 12px;
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
          font-size: 12px;
          line-height: 1.4;
          pointer-events: none;
        }
        .gantt-tooltip-title {
          font-weight: 700;
          margin-bottom: 6px;
        }
        .gantt_skeleton_column
      `}</style>
      <div style={{ position: 'relative' }}>
        {hoverTooltip && (
          <div
            className="gantt-tooltip"
            style={{ top: hoverTooltip.y, left: hoverTooltip.x }}
          >
            {/* <div className="gantt-tooltip-title">{hoverTooltip.title}</div> */}
            {hoverTooltip.lines.map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
          </div>
        )}
        <div
          ref={containerRef}
          style={{ height: '70vh', width: '100%', border: '1px solid #ccc' }}
        />
      </div>
    </div>
  );
};

export default BasicGanttChart;

// #1976D2 #4CAF50
// #2196F3 #3f8F50
